/**
 * Force-update version gate — native apps only (Android/iOS via Capacitor).
 * Compares the installed app's version (from @capacitor/app, same source
 * useAppVersion() in deviceInfo.js already reads) against the per-tenant
 * minimum configured in Super Admin, and blocks the app if it's too old.
 *
 * Web has no app-store concept, so this never applies there.
 */
import { getRoleForPath } from "./authStorage";

/** Dotted-numeric version compare — matches the "x.y.z" format
 * scripts/build_app.py's --version-name already writes into native builds.
 * Returns true only when `installed` is strictly lower than `required`. */
export function isVersionBelow(installed, required) {
  if (!required) return false;   // no minimum configured — never blocks
  if (!installed) return false;  // couldn't read installed version — fail open
  const a = String(installed).split(".").map((n) => parseInt(n, 10) || 0);
  const b = String(required).split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const av = a[i] || 0, bv = b[i] || 0;
    if (av < bv) return true;
    if (av > bv) return false;
  }
  return false;
}

/** "android" | "ios" | "web" — same lookup used by lib/geolocation.js / lib/pushNotifications.js. */
export function getNativePlatform() {
  return (typeof window !== "undefined" && window.Capacitor?.getPlatform?.()) || "web";
}

/** "customer" | "rider" — reuses the SAME path-prefix→role map every other
 * realm-aware helper (authStorage, auth.jsx) already relies on, rather than
 * inventing a second way to detect persona. Native builds always start on a
 * persona-specific path (build_app.py bakes /delivery/login into the rider
 * app's start URL), so this resolves correctly from first render. */
export function getPersona(pathname) {
  const p = pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return getRoleForPath(p) === "delivery_boy" ? "rider" : "customer";
}

/** Reads the right one of the 4 tenant.<platform>_<persona>_min_version fields. */
export function getRequiredMinVersion(tenant, platform, persona) {
  if (!tenant || (platform !== "android" && platform !== "ios")) return "";
  return tenant[`${platform}_${persona}_min_version`] || "";
}

/** Where "Update Now" should send the user. Android is derived from the
 * running app's own package id (no per-tenant field needed); iOS needs the
 * App Store Connect URL, which super admin sets explicitly per persona. */
export function getStoreUrl(tenant, platform, persona, appId) {
  if (platform === "android") {
    return appId ? `https://play.google.com/store/apps/details?id=${appId}` : "";
  }
  if (platform === "ios") {
    return (tenant && tenant[`ios_${persona}_store_url`]) || "";
  }
  return "";
}
